import { motion } from "framer-motion";
import { BookOpen, ArrowRight, Sparkles } from "lucide-react";
import { imageUrl } from "@/lib/api";
import { ShareControls } from "@/components/ShareControls";

// Default premium "physical book" scene. Structured so book-type-specific scenes
// (children's reading nook, cookbook kitchen, etc.) can be swapped in later.
const BACKDROP = `${process.env.PUBLIC_URL}/showcase/bookstore-backdrop.jpg`;

export const BookShowcase = ({ book, onOpen, onBack, onBookChange }) => {
  const cover = book?.cover_image_id ? `${imageUrl(book.cover_image_id)}?t=${book.cover_image_id}` : null;
  const square = book?.mode === "children";
  const bookH = square ? 360 : 440;
  const bookW = Math.round(bookH * (square ? 1 : 6 / 9));

  return (
    <div data-testid="book-showcase" className="relative rounded-3xl overflow-hidden border border-gold-500/12 shadow-2xl min-h-[82vh]">
      <div className="absolute inset-0 bg-center bg-cover scale-105" style={{ backgroundImage: `url(${BACKDROP})` }} />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950/70 via-navy-950/55 to-navy-950/85" />

      <div className="relative z-10 flex flex-col items-center text-center px-6 py-14 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-gold-400/30 bg-white/5 backdrop-blur px-4 py-1.5 mb-10"
        >
          <Sparkles className="w-3.5 h-3.5 text-gold-300" />
          <span className="font-ui text-[11px] uppercase tracking-[0.3em] text-gold-200">See it in the world</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40, rotateY: -32 }} animate={{ opacity: 1, y: 0, rotateY: -16 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="kbook-stage"
        >
          <div className="kbook kbook-float">
            <div className="vol" style={{ width: bookW, height: bookH }}>
              {cover ? (
                <img src={cover} alt={`${book.title} cover`} data-testid="showcase-cover" draggable={false} />
              ) : (
                <div className="w-full h-full grid place-items-center bg-gradient-to-b from-navy-800 to-navy-950">
                  <BookOpen className="w-10 h-10 text-gold-300/70" />
                </div>
              )}
            </div>
            <div className="kbook-reflection" style={{ width: bookW, height: bookH, backgroundImage: cover ? `url(${cover})` : "none" }} />
          </div>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.45 }}
          className="font-display text-3xl sm:text-4xl font-semibold text-white mt-12 max-w-2xl drop-shadow"
        >
          {book?.title}
        </motion.h2>
        {book?.subtitle && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
            className="font-read italic text-slate-200/90 mt-2 max-w-xl">{book.subtitle}</motion.p>
        )}
        {book?.author && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="font-ui text-sm tracking-wide text-gold-200/90 mt-3">by {book.author}</motion.p>
        )}

        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.7 }}
          className="flex flex-wrap items-center justify-center gap-3 mt-9"
        >
          <button data-testid="showcase-open-book" onClick={onOpen}
            className="klyr-btn-metal inline-flex items-center gap-2 font-ui text-sm font-semibold rounded-xl px-7 py-3.5 transition-[filter]">
            <BookOpen className="w-4 h-4" /> Open Book <ArrowRight className="w-4 h-4" />
          </button>
          <button data-testid="showcase-back" onClick={onBack}
            className="inline-flex items-center gap-2 font-ui text-sm text-slate-200 border border-white/20 hover:border-gold-400/60 rounded-xl px-5 py-3.5 transition-colors">
            Back to Library
          </button>
          <ShareControls book={book} onChange={onBookChange} />
        </motion.div>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
          className="font-ui text-[11px] text-slate-400 mt-7">
          A visualization of your finished book — not an actual store listing.
        </motion.p>
      </div>
    </div>
  );
};
